import React, { useState } from 'react';
import { useToast } from '../../Toast';

interface MarketingTabProps {}

interface ProfileData {
  name: string;
  specialty: string;
  therapyFinderUrl: string;
  isPublished: boolean;
  introduction: string;
  contactOptions: string;
  insuranceAccepted: string;
  fees: string;
  focusAreas: string[];
  approaches: string[];
  appointmentTypes: string[];
  nextAvailability: string;
  highlights: {
    acceptingNewClients: boolean;
    availableOnNights: boolean;
    availableOnWeekends: boolean;
    offersFreConsultation: boolean;
    offersTelehealth: boolean;
    offersInPerson: boolean;
    acceptsOnlinePayments: boolean;
    acceptsInsurance: boolean;
  };
  ageGroups: string[];
  communities: string;
  languages: string;
  faiths: string;
  socialMedia: {
    facebook?: string;
    twitter?: string;
    instagram?: string;
    threads?: string;
    youtube?: string;
    linkedin?: string;
  };
}

const MarketingTab: React.FC<MarketingTabProps> = () => {
  const toast = useToast();
  const [activeSubTab, setActiveSubTab] = useState<'directory' | 'profile' | 'website' | 'reviews'>('directory');
  const [directoryEnabled, setDirectoryEnabled] = useState(true);
  const [showEditSection, setShowEditSection] = useState<string | null>(null);

  // TODO: Load from Firestore provider profile
  const [profileData, setProfileData] = useState<ProfileData>({
    name: '',
    specialty: '',
    therapyFinderUrl: '',
    isPublished: false,
    introduction: '',
    contactOptions: 'Contact form and online appointment request settings',
    insuranceAccepted: 'For example: Aetna, Anthem, Cigna',
    fees: 'For example: Half Hour Session: $100, One Hour Session: $250-$350',
    focusAreas: ['Anxiety', 'Depression', 'Trauma'],
    approaches: ['Behavioral Therapy', 'Cognitive Behavioral Therapy (CBT)', 'Family/Marital Therapy'],
    appointmentTypes: ['Individuals'],
    nextAvailability: 'For example: Friday, 12/31',
    highlights: {
      acceptingNewClients: true,
      availableOnNights: true,
      availableOnWeekends: true,
      offersFreConsultation: false,
      offersTelehealth: true,
      offersInPerson: false,
      acceptsOnlinePayments: false,
      acceptsInsurance: false
    },
    ageGroups: ['Children (6-12)', 'Adults (24+)'],
    communities: 'For example: Military/Veterans, People with Disabilities, Racial Justice/Equity',
    languages: 'For example: English, Spanish',
    faiths: 'For example: Agnostic, Multi-Faith',
    socialMedia: {}
  });

  const renderDirectoryTab = () => (
    <div>
      {/* Directory Status Card */}
      <div style={{
        backgroundColor: '#28527A',
        borderRadius: '8px',
        padding: '2rem',
        marginBottom: '2rem',
        border: '1px solid #6CA6C1'
      }}>
        <h3 style={{ color: '#e5e7eb', marginBottom: '1rem', fontSize: '1.25rem' }}>
          Therapy Finder Directory is currently turned {directoryEnabled ? 'on' : 'off'} for your practice
        </h3>
        <p style={{ color: '#9CA3AF', marginBottom: '2rem' }}>
          You can now edit and publish your profile in the directory.
        </p>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <button
            onClick={() => setDirectoryEnabled(false)}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: 'transparent',
              color: '#EF4444',
              border: '2px solid #EF4444',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.95rem',
              fontWeight: '500'
            }}
          >
            Turn off directory access
          </button>
          <button
            onClick={() => setActiveSubTab('profile')}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#3B82F6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.95rem',
              fontWeight: '500'
            }}
          >
            Edit your profile
          </button>
        </div>
      </div>
    </div>
  );

  const renderProfileTab = () => (
    <div>
      {/* Info Banner */}
      <div style={{
        backgroundColor: '#EFF6FF',
        border: '1px solid #BFDBFE',
        borderRadius: '8px',
        padding: '1rem',
        marginBottom: '2rem',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem'
      }}>
        <span style={{ color: '#3B82F6', fontSize: '1.25rem' }}>ℹ️</span>
        <p style={{ color: '#1E40AF', margin: 0, fontSize: '0.95rem' }}>
          The information added below will be visible to current and prospective clients. It will be displayed on Therapy Finder and your practice's Professional Website.
        </p>
      </div>

      {/* Profile Header */}
      <div style={{
        backgroundColor: '#28527A',
        borderRadius: '8px',
        padding: '2rem',
        marginBottom: '2rem',
        border: '1px solid #6CA6C1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          {/* Avatar */}
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: '#E57373',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '2rem',
            fontWeight: 'bold',
            color: 'white'
          }}>
            {profileData.name ? profileData.name.split(' ').map(n => n[0]).join('') : '?'}
          </div>

          {/* Profile Info */}
          <div>
            <h3 style={{ color: '#e5e7eb', margin: '0 0 0.5rem 0', fontSize: '1.5rem' }}>
              {profileData.name || 'Your Name'}
            </h3>
            <p style={{ color: '#9CA3AF', margin: '0 0 0.5rem 0' }}>{profileData.specialty || 'Your Specialty'}</p>

            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ color: '#e5e7eb', fontSize: '0.9rem', fontWeight: '500' }}>Therapy Finder</span>
                <span style={{
                  backgroundColor: '#F59E0B',
                  color: 'white',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}>
                  Unpublished
                </span>
              </div>
              <a href={`https://${profileData.therapyFinderUrl}`} style={{
                color: '#3B82F6',
                fontSize: '0.9rem',
                textDecoration: 'none'
              }}>
                {profileData.therapyFinderUrl}
              </a>
            </div>
            <p style={{ color: '#9CA3AF', fontSize: '0.85rem', margin: '0.5rem 0 0 0', fontStyle: 'italic' }}>
              Your profiles will be inactive until you publish.
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '1rem' }}>
          <button
            onClick={() => setShowEditSection('basicInfo')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#1F2937',
              color: '#e5e7eb',
              border: '1px solid #6CA6C1',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Edit
          </button>
          <button
            onClick={() => toast.info('Coming Soon', 'Profile preview will open in a new tab once published.')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#6B7280',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Preview
          </button>
          <button
            onClick={() => setProfileData({ ...profileData, isPublished: true })}
            style={{
              padding: '0.5rem 1.5rem',
              backgroundColor: '#E5E7EB',
              color: '#1F2937',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: '500'
            }}
          >
            Publish
          </button>
        </div>
      </div>

      {/* Publish Profile Section */}
      <div style={{
        backgroundColor: '#DBEAFE',
        borderRadius: '8px',
        padding: '2rem',
        marginBottom: '2rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '2rem' }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ color: '#1E3A8A', margin: '0 0 1rem 0', fontSize: '1.25rem' }}>
              Publish your Therapy Finder profile
            </h3>
            <p style={{ color: '#1E40AF', margin: 0, fontSize: '0.95rem', lineHeight: '1.6' }}>
              Publish your profile in just 4 steps
            </p>
            <ul style={{ color: '#1E40AF', marginTop: '1rem', paddingLeft: '1.5rem' }}>
              <li style={{ marginBottom: '0.5rem' }}>
                <span style={{ fontWeight: '600' }}>1</span> Choose your specialties
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <span style={{ fontWeight: '600' }}>2</span> Select your accepted insurances
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <span style={{ fontWeight: '600' }}>3</span> Enable contact form or online appointments
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <span style={{ fontWeight: '600' }}>4</span> Add your bio
              </li>
            </ul>
            <button
              onClick={() => setShowEditSection('basicInfo')}
              style={{
                marginTop: '1.5rem',
                padding: '0.75rem 1.5rem',
                backgroundColor: '#3B82F6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: '500'
              }}
            >
              Continue
            </button>
          </div>
          <div style={{ width: '200px', textAlign: 'center' }}>
            {/* Illustration placeholder */}
            <div style={{
              width: '100%',
              height: '150px',
              backgroundColor: '#93C5FD',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#1E3A8A'
            }}>
              📋✨
            </div>
          </div>
        </div>
      </div>

      {/* Basic Information */}
      <div style={{
        backgroundColor: '#28527A',
        borderRadius: '8px',
        padding: '2rem',
        marginBottom: '1.5rem',
        border: '1px solid #6CA6C1'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h4 style={{ color: '#e5e7eb', margin: 0, fontSize: '1.1rem' }}>Basic Information</h4>
          <button
            onClick={() => setShowEditSection(showEditSection === 'basicInfo' ? null : 'basicInfo')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#1F2937',
              color: '#e5e7eb',
              border: '1px solid #6CA6C1',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Edit
          </button>
        </div>

        {/* Contact Options */}
        <div style={{ marginBottom: '2rem' }}>
          <h5 style={{ color: '#9CA3AF', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Contact options</h5>
          <p style={{ color: '#e5e7eb', margin: 0 }}>{profileData.contactOptions}</p>
        </div>

        {/* Your Introduction */}
        <div style={{ marginBottom: '2rem' }}>
          <h5 style={{ color: '#9CA3AF', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Your Introduction</h5>
          <div style={{
            backgroundColor: '#FEF3C7',
            border: '1px solid #FCD34D',
            borderRadius: '6px',
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.5rem'
          }}>
            <span style={{ fontSize: '1rem' }}>💡</span>
            <p style={{ color: '#92400E', margin: 0, fontSize: '0.85rem' }}>
              Write a few sentences to tell potential clients how you can help them. <span onClick={() => setShowEditSection('basicInfo')} style={{ color: '#1E40AF', textDecoration: 'underline', cursor: 'pointer' }}>Edit</span>
            </p>
          </div>
          <p style={{ color: '#e5e7eb', margin: 0, whiteSpace: 'pre-line' }}>{profileData.introduction}</p>
          <p style={{ color: '#9CA3AF', fontSize: '0.85rem', margin: '1rem 0 0 0', fontStyle: 'italic' }}>
            Add an optional intro video
          </p>
        </div>
      </div>

      {/* Insurance and Fees */}
      <div style={{
        backgroundColor: '#28527A',
        borderRadius: '8px',
        padding: '2rem',
        marginBottom: '1.5rem',
        border: '1px solid #6CA6C1'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h4 style={{ color: '#e5e7eb', margin: 0, fontSize: '1.1rem' }}>Insurance and Fees</h4>
          <button
            onClick={() => setShowEditSection(showEditSection === 'insurance' ? null : 'insurance')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#1F2937',
              color: '#e5e7eb',
              border: '1px solid #6CA6C1',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Edit
          </button>
        </div>

        <div style={{
          backgroundColor: '#FEF3C7',
          border: '1px solid #FCD34D',
          borderRadius: '6px',
          padding: '0.75rem 1rem',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.5rem'
        }}>
          <span style={{ fontSize: '1rem' }}>💡</span>
          <p style={{ color: '#92400E', margin: 0, fontSize: '0.85rem' }}>
            If you'd like insurance and fees to be hidden from your public profile, edit and delete all items from the list.
          </p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h5 style={{ color: '#9CA3AF', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Insurance Accepted</h5>
          <p style={{ color: '#e5e7eb', margin: 0, fontStyle: 'italic' }}>{profileData.insuranceAccepted}</p>
        </div>

        <div>
          <h5 style={{ color: '#9CA3AF', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Fees</h5>
          <p style={{ color: '#e5e7eb', margin: 0, fontStyle: 'italic' }}>{profileData.fees}</p>
        </div>
      </div>

      {/* Services */}
      <div style={{
        backgroundColor: '#28527A',
        borderRadius: '8px',
        padding: '2rem',
        marginBottom: '1.5rem',
        border: '1px solid #6CA6C1'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h4 style={{ color: '#e5e7eb', margin: 0, fontSize: '1.1rem' }}>Services</h4>
          <button
            onClick={() => setShowEditSection(showEditSection === 'services' ? null : 'services')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#1F2937',
              color: '#e5e7eb',
              border: '1px solid #6CA6C1',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Edit
          </button>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h5 style={{ color: '#9CA3AF', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Focus Areas</h5>
          <p style={{ color: '#e5e7eb', margin: 0 }}>{profileData.focusAreas.join(', ')}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h5 style={{ color: '#9CA3AF', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Approaches</h5>
          <p style={{ color: '#e5e7eb', margin: 0 }}>{profileData.approaches.join(', ')}</p>
        </div>

        <div>
          <h5 style={{ color: '#9CA3AF', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Appointment Types</h5>
          <p style={{ color: '#e5e7eb', margin: 0 }}>{profileData.appointmentTypes.join(', ')}</p>
        </div>
      </div>

      {/* Highlights */}
      <div style={{
        backgroundColor: '#28527A',
        borderRadius: '8px',
        padding: '2rem',
        marginBottom: '1.5rem',
        border: '1px solid #6CA6C1'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h4 style={{ color: '#e5e7eb', margin: 0, fontSize: '1.1rem' }}>Highlights</h4>
          <button
            onClick={() => setShowEditSection(showEditSection === 'highlights' ? null : 'highlights')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#1F2937',
              color: '#e5e7eb',
              border: '1px solid #6CA6C1',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Edit
          </button>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h5 style={{ color: '#9CA3AF', fontSize: '0.9rem', marginBottom: '1rem' }}>Next availability:</h5>
          <p style={{ color: '#e5e7eb', margin: 0, fontStyle: 'italic' }}>{profileData.nextAvailability}</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          {Object.entries({
            'Accepting New Clients': profileData.highlights.acceptingNewClients,
            'Available on Nights': profileData.highlights.availableOnNights,
            'Available on Weekends': profileData.highlights.availableOnWeekends,
            'Offers Free Consultation': profileData.highlights.offersFreConsultation,
            'Offers Telehealth Appointments': profileData.highlights.offersTelehealth,
            'Offers In-Person Appointments': profileData.highlights.offersInPerson,
            'Accepts Online Payments': profileData.highlights.acceptsOnlinePayments,
            'Accepts Insurance': profileData.highlights.acceptsInsurance
          }).map(([label, value]) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                backgroundColor: value ? '#10B981' : '#6B7280',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '0.75rem'
              }}>
                {value ? '✓' : ''}
              </span>
              <span style={{ color: '#e5e7eb', fontSize: '0.9rem' }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Population Focus */}
      <div style={{
        backgroundColor: '#28527A',
        borderRadius: '8px',
        padding: '2rem',
        marginBottom: '1.5rem',
        border: '1px solid #6CA6C1'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h4 style={{ color: '#e5e7eb', margin: 0, fontSize: '1.1rem' }}>Population Focus</h4>
          <button
            onClick={() => setShowEditSection(showEditSection === 'population' ? null : 'population')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#1F2937',
              color: '#e5e7eb',
              border: '1px solid #6CA6C1',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Edit
          </button>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h5 style={{ color: '#9CA3AF', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Age Groups</h5>
          <p style={{ color: '#e5e7eb', margin: 0 }}>{profileData.ageGroups.join(', ')}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h5 style={{ color: '#9CA3AF', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Communities</h5>
          <p style={{ color: '#e5e7eb', margin: 0, fontStyle: 'italic' }}>{profileData.communities}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h5 style={{ color: '#9CA3AF', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Languages</h5>
          <p style={{ color: '#e5e7eb', margin: 0, fontStyle: 'italic' }}>{profileData.languages}</p>
        </div>

        <div>
          <h5 style={{ color: '#9CA3AF', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Faiths</h5>
          <p style={{ color: '#e5e7eb', margin: 0, fontStyle: 'italic' }}>{profileData.faiths}</p>
        </div>
      </div>

      {/* Social Media */}
      <div style={{
        backgroundColor: '#28527A',
        borderRadius: '8px',
        padding: '2rem',
        marginBottom: '1.5rem',
        border: '1px solid #6CA6C1'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h4 style={{ color: '#e5e7eb', margin: 0, fontSize: '1.1rem' }}>Social Media</h4>
          <button
            onClick={() => setShowEditSection(showEditSection === 'social' ? null : 'social')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#1F2937',
              color: '#e5e7eb',
              border: '1px solid #6CA6C1',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Edit
          </button>
        </div>

        <p style={{ color: '#e5e7eb', margin: 0, fontStyle: 'italic' }}>For example:</p>
        <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem', color: '#9CA3AF' }}>
          <span>📘 Facebook</span>
          <span>𝕏 X (Twitter)</span>
          <span>📷 Instagram</span>
          <span>🧵 Threads</span>
          <span>▶️ YouTube</span>
          <span>💼 LinkedIn</span>
        </div>
      </div>
    </div>
  );

  const comingSoonBadge = (
    <span style={{
      display: 'inline-block',
      backgroundColor: '#F59E0B',
      color: '#000',
      fontSize: '0.7rem',
      fontWeight: 700,
      padding: '2px 8px',
      borderRadius: '10px',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
      marginLeft: '8px',
      verticalAlign: 'middle'
    }}>
      Coming Soon
    </span>
  );

  const renderWebsiteTab = () => (
    <div>
      <div style={{
        backgroundColor: '#28527A',
        borderRadius: '8px',
        padding: '2rem',
        textAlign: 'center',
        border: '1px solid #6CA6C1',
        opacity: 0.7
      }}>
        <h3 style={{ color: '#e5e7eb', marginBottom: '1rem' }}>
          Website and Domain {comingSoonBadge}
        </h3>
        <p style={{ color: '#9CA3AF', marginBottom: '1rem' }}>
          Create a professional website for your practice with a custom domain.
        </p>
        <p style={{ color: '#F59E0B', fontSize: '0.9rem', marginBottom: '2rem' }}>
          This feature is under development and will be available in a future update.
        </p>
      </div>
    </div>
  );

  const renderReviewsTab = () => (
    <div>
      <div style={{
        backgroundColor: '#28527A',
        borderRadius: '8px',
        padding: '2rem',
        textAlign: 'center',
        border: '1px solid #6CA6C1',
        opacity: 0.7
      }}>
        <h3 style={{ color: '#e5e7eb', marginBottom: '1rem' }}>
          Client Reviews & Ratings {comingSoonBadge}
        </h3>
        <p style={{ color: '#9CA3AF', marginBottom: '1rem' }}>
          Collect and display client reviews to build trust with prospective clients.
          Manage your reputation with review invitations and response tools.
        </p>
        <p style={{ color: '#F59E0B', fontSize: '0.9rem', marginBottom: '2rem' }}>
          This feature is under development and will be available in a future update.
        </p>
      </div>
    </div>
  );

  return (
    <div style={{ padding: '2rem' }}>
      {/* Page Title */}
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ color: '#e5e7eb', marginBottom: '0.5rem', fontSize: '2rem' }}>
          Marketing
          <span style={{
            display: 'inline-block',
            backgroundColor: '#F59E0B',
            color: '#000',
            fontSize: '0.75rem',
            fontWeight: 700,
            padding: '4px 12px',
            borderRadius: '12px',
            textTransform: 'uppercase' as const,
            letterSpacing: '0.5px',
            marginLeft: '12px',
            verticalAlign: 'middle'
          }}>
            Coming Soon
          </span>
        </h2>
        <p style={{ color: '#9CA3AF', margin: 0 }}>
          MyClinicCore provides access to a public directory profile, custom website, and more so you can attract the type of clients you're looking for.
        </p>
      </div>

      {/* Coming Soon Content */}
      <div style={{
        backgroundColor: '#28527A',
        borderRadius: '12px',
        padding: '3rem 2rem',
        textAlign: 'center',
        border: '1px solid #6CA6C1',
      }}>
        <div style={{ fontSize: '3rem', marginBottom: '1.5rem' }}>&#128640;</div>
        <h3 style={{ color: '#e5e7eb', marginBottom: '1rem', fontSize: '1.5rem' }}>
          Marketing Tools Are On The Way
        </h3>
        <p style={{ color: '#d1d5db', fontSize: '1rem', maxWidth: '600px', margin: '0 auto 2rem', lineHeight: '1.6' }}>
          We're building powerful marketing tools to help grow your practice. These features will be available in an upcoming update.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '1.5rem',
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          <div style={{
            backgroundColor: '#16325B',
            borderRadius: '8px',
            padding: '1.5rem',
            border: '1px solid #374151'
          }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>&#128218;</div>
            <h4 style={{ color: '#f9fafb', marginBottom: '0.5rem', fontSize: '1rem' }}>Provider Directory</h4>
            <p style={{ color: '#9ca3af', fontSize: '0.85rem', margin: 0 }}>
              List your practice in a searchable directory for new clients
            </p>
          </div>

          <div style={{
            backgroundColor: '#16325B',
            borderRadius: '8px',
            padding: '1.5rem',
            border: '1px solid #374151'
          }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>&#127760;</div>
            <h4 style={{ color: '#f9fafb', marginBottom: '0.5rem', fontSize: '1rem' }}>Practice Website</h4>
            <p style={{ color: '#9ca3af', fontSize: '0.85rem', margin: 0 }}>
              Create a professional website with custom domain
            </p>
          </div>

          <div style={{
            backgroundColor: '#16325B',
            borderRadius: '8px',
            padding: '1.5rem',
            border: '1px solid #374151'
          }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>&#11088;</div>
            <h4 style={{ color: '#f9fafb', marginBottom: '0.5rem', fontSize: '1rem' }}>Client Reviews</h4>
            <p style={{ color: '#9ca3af', fontSize: '0.85rem', margin: 0 }}>
              Collect and display reviews to build trust with prospective clients
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketingTab;
